/** Helpers shared by the survey builder: key generation, defaults, validation. */

import type { SurveyQuestion, SurveyQuestionType } from "../../lib/api";

/** `"What do you mainly use DeeX for?"` → `"what_do_you_mainly_use_deex_for"`. */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
}

/**
 * A question's key is what responses point at, so it has to be unique within the survey and
 * stable for the survey's life. Generated once from the prompt; never regenerated on edit.
 */
export function uniqueKey(base: string, taken: Set<string>): string {
  const root = slugify(base) || "question";
  if (!taken.has(root)) return root;
  let n = 2;
  while (taken.has(`${root}_${n}`)) n += 1;
  return `${root}_${n}`;
}

export function uniqueOptionId(label: string, taken: Set<string>): string {
  const root = slugify(label) || "option";
  if (!taken.has(root)) return root;
  let n = 2;
  while (taken.has(`${root}_${n}`)) n += 1;
  return `${root}_${n}`;
}

/** A fresh question of the given type, with type-appropriate defaults filled in. */
export function blankQuestion(
  type: SurveyQuestionType,
  order: number,
  takenKeys: Set<string>
): SurveyQuestion {
  const base: SurveyQuestion = {
    key: uniqueKey(`question_${order + 1}`, takenKeys),
    prompt: "",
    type,
    order,
    required: true,
  };

  switch (type) {
    case "SINGLE_CHOICE":
    case "MULTI_CHOICE":
      return {
        ...base,
        options: [
          { id: "option_1", label: "" },
          { id: "option_2", label: "" },
        ],
        allowOther: false,
        otherPlaceholder: null,
        maxSelections: null,
      };
    case "SHORT_TEXT":
      return { ...base, placeholder: "", maxLength: 240, suggestions: [] };
    case "LONG_TEXT":
      return { ...base, placeholder: "", maxLength: 600, suggestions: [] };
    case "NPS":
      return {
        ...base,
        scale: {
          min: 0,
          max: 10,
          minLabel: "Not likely",
          maxLabel: "Very likely",
        },
      };
    case "RATING":
      return {
        ...base,
        scale: { min: 1, max: 5, minLabel: "Poor", maxLabel: "Excellent" },
      };
    default:
      return base;
  }
}

/**
 * Reshapes a question when its type changes, keeping the prompt and key but dropping fields
 * that no longer apply — otherwise a choice question turned into text keeps stale options
 * that would be sent to the API.
 */
export function retypeQuestion(
  question: SurveyQuestion,
  type: SurveyQuestionType
): SurveyQuestion {
  const fresh = blankQuestion(type, question.order, new Set());
  return {
    ...fresh,
    key: question.key,
    prompt: question.prompt,
    helpText: question.helpText ?? null,
    required: question.required,
  };
}

export type BuilderIssue = { questionKey?: string; message: string };

/** Everything that must be true before a survey can be saved. */
export function validateBuilder(
  title: string,
  questions: SurveyQuestion[]
): BuilderIssue[] {
  const issues: BuilderIssue[] = [];

  if (!title.trim()) issues.push({ message: "Give the survey a title." });
  if (questions.length === 0) {
    issues.push({ message: "Add at least one question." });
  }

  questions.forEach((q, i) => {
    const where = `Question ${i + 1}`;
    if (!q.prompt.trim()) {
      issues.push({ questionKey: q.key, message: `${where} needs a prompt.` });
    }

    if (q.type === "SINGLE_CHOICE" || q.type === "MULTI_CHOICE") {
      const filled = (q.options ?? []).filter((o) => o.label.trim());
      if (filled.length < 2) {
        issues.push({
          questionKey: q.key,
          message: `${where} needs at least two options.`,
        });
      }
      const labels = filled.map((o) => o.label.trim().toLowerCase());
      if (new Set(labels).size !== labels.length) {
        issues.push({
          questionKey: q.key,
          message: `${where} has duplicate options.`,
        });
      }
    }
  });

  return issues;
}

/** Strips empty options/suggestions and renumbers `order` before sending to the API. */
export function normalizeQuestions(
  questions: SurveyQuestion[]
): SurveyQuestion[] {
  return questions.map((q, i) => {
    const out: SurveyQuestion = { ...q, order: i, prompt: q.prompt.trim() };
    if (out.options) {
      out.options = out.options
        .filter((o) => o.label.trim())
        .map((o) => ({ ...o, label: o.label.trim() }));
    }
    if (out.suggestions) {
      out.suggestions = out.suggestions
        .map((s) => ({ ...s, label: s.label.trim() }))
        .filter((s) => s.label);
    }
    return out;
  });
}
